import { Component, OnInit, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { Oauth2Service } from '../../services/oauth2.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { SessionService } from '../../services/session.service';
import { AuthErrorHandlerService } from '../../services/auth-error-handler.service';
import { UserService } from '../../../user/services/user.service';

@Component({
  selector: 'app-oauth-callback',
  template: `
    <div class="oauth-callback-container">
      <div class="spinner"></div>
      <p>{{ message }}</p>
    </div>
  `,
  styles: [`
    .oauth-callback-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      gap: 20px;
    }
    .spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #3498db;
      border-radius: 50%;
      animation: spin 2s linear infinite;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `]
})
export class OauthCallbackComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _oauth2Service = inject(Oauth2Service);
  private readonly _tokenStorage = inject(TokenStorageService);
  private readonly _sessionService = inject(SessionService);
  private readonly _userService = inject(UserService);
  private readonly _authErrorHandler = inject(AuthErrorHandlerService);
  
  message = 'Connexion en cours...';

  async ngOnInit(): Promise<void> {
    const uniqueCode = this._route.snapshot.queryParamMap.get('_et');
    
    if (!uniqueCode) {
      this._handleAuthError('Code d\'authentification manquant');
      return;
    }

    try {
      await this._processOAuthCallback(uniqueCode);
    } catch (error) {
      const authError = this._authErrorHandler.handleAuthError(error);
      console.error('OAuth callback failed:', authError);
      this._handleAuthError(authError.message || 'Erreur de connexion');
    }
  }

  private async _processOAuthCallback(uniqueCode: string): Promise<void> {
    try {
      const tokenResponse = await firstValueFrom(this._oauth2Service.fetchToken(uniqueCode));
      
      this._tokenStorage.setAccessToken(tokenResponse.accessToken);
      
      await firstValueFrom(this._userService.fetchUser());
      
      this._sessionService.setAuthenticated();
      
      this.message = 'Connexion réussie, redirection...';
      
      this._cleanUrlAndRedirect();
    } catch (error) {
      const authError = this._authErrorHandler.handleAuthError(error);
      if (authError.shouldLogout) {
        this._tokenStorage.clearToken();
      }
      throw authError;
    }
  }

  private _cleanUrlAndRedirect(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('_et');
    window.history.replaceState({}, document.title, url.pathname);
    
    this._router.navigate(['/']);
  }

  private _handleAuthError(errorMessage: string): void {
    this.message = errorMessage;
    this._tokenStorage.clearToken();
    
    setTimeout(() => {
      this._router.navigate(['/auth']);
    }, 2000);
  }
}